"use client";

import { useState, useCallback, useMemo } from "react";
import {
	Heart,
	Users,
	FileText,
	ImageIcon,
	Music,
	CheckCircle2,
	ChevronLeft,
	ChevronRight,
	Plus,
	X,
	Loader2,
	Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { PagePreview } from "./page-preview";
import { PaymentModal } from "./payment-modal";
import {
	DndContext,
	closestCenter,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	rectSortingStrategy,
	useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Logo } from "@/components/logo";
import { ModeToggle } from "@/components/mode-toggle";
import { LanguageToggle } from "@/components/language-toggle";
import { env } from "@/lib/env";
import { useAnalytics } from "@/lib/analytics";
import { formatPriceBRL, getExtraPhotoCount, getPageTotalPrice } from "@/lib/pricing";
import { useTranslations } from "next-intl";
import { useDropzone } from "react-dropzone";

export type FormData = {
	slug: string;
	coupleName1: string;
	coupleName2: string;
	title: string;
	message: string;
	relationshipDate: string;
	imageFiles: File[];
	imagePreviewUrls: string[];
	musicUrl: string;
	theme: "soft" | "classic" | "minimal";
};

const STEP_ICONS = [Users, FileText, ImageIcon, Music, CheckCircle2];

function SortableImageItem({
	id,
	url,
	onRemove,
	removeLabel,
}: {
	id: string;
	url: string;
	onRemove: () => void;
	removeLabel: string;
}) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={cn(
				"group relative aspect-square overflow-hidden rounded-lg border border-border",
				isDragging && "z-50 opacity-80 shadow-lg",
			)}
			{...attributes}
			{...listeners}
		>
			{/* biome-ignore lint/performance/noImgElement: blob URL; S3 upload only after payment */}
			<img
				src={url}
				alt=""
				className="h-full w-full cursor-grab active:cursor-grabbing object-cover"
			/>
			<button
				type="button"
				onClick={(e) => {
					e.stopPropagation();
					onRemove();
				}}
				className="absolute right-1 top-1 cursor-pointer rounded-full bg-destructive/90 p-1 text-background"
				aria-label={removeLabel}
			>
				<X className="h-3.5 w-3.5" />
			</button>
		</div>
	);
}

const initialForm: FormData = {
	slug: "",
	coupleName1: "",
	coupleName2: "",
	title: "",
	message: "",
	relationshipDate: "",
	imageFiles: [],
	imagePreviewUrls: [],
	musicUrl: "",
	theme: "soft",
};

export default function CriarPage() {
	const t = useTranslations("CreatePage");
	const [step, setStep] = useState(0);
	const [form, setForm] = useState<FormData>(initialForm);
	const [couponCode, setCouponCode] = useState("");
	const { trackFormStep, trackCheckoutStarted } = useAnalytics();

	const STEPS = useMemo(
		() => [
			{ id: 0, label: t("steps.identification"), icon: STEP_ICONS[0] },
			{ id: 1, label: t("steps.content"), icon: STEP_ICONS[1] },
			{ id: 2, label: t("steps.photos"), icon: STEP_ICONS[2] },
			{ id: 3, label: t("steps.music"), icon: STEP_ICONS[3] },
			{ id: 4, label: t("steps.review"), icon: STEP_ICONS[4] },
		],
		[t],
	);

	const STEP_DESCRIPTIONS = useMemo(
		() => [
			t("stepDescriptions.identification"),
			t("stepDescriptions.content"),
			t("stepDescriptions.photos"),
			t("stepDescriptions.music"),
			t("stepDescriptions.review"),
		],
		[t],
	);

	const update = useCallback((patch: Partial<FormData>) => {
		setForm((prev) => ({ ...prev, ...patch }));
	}, []);

	const addImages = useCallback(
		(files: File[]) => {
			if (!files.length) return;
			const newFiles = [...form.imageFiles, ...files];
			const newUrls = newFiles.map((f) => URL.createObjectURL(f));
			form.imagePreviewUrls.forEach(URL.revokeObjectURL);
			setForm((prev) => ({
				...prev,
				imageFiles: newFiles,
				imagePreviewUrls: newUrls,
			}));
		},
		[form.imageFiles, form.imagePreviewUrls],
	);

	const removeImage = useCallback(
		(index: number) => {
			const newFiles = form.imageFiles.filter((_, i) => i !== index);
			URL.revokeObjectURL(form.imagePreviewUrls[index] ?? "");
			setForm((prev) => ({
				...prev,
				imageFiles: newFiles,
				imagePreviewUrls: prev.imagePreviewUrls.filter((_, i) => i !== index),
			}));
		},
		[form.imageFiles, form.imagePreviewUrls],
	);

	const reorderImages = useCallback((oldIndex: number, newIndex: number) => {
		setForm((prev) => ({
			...prev,
			imageFiles: arrayMove(prev.imageFiles, oldIndex, newIndex),
			imagePreviewUrls: arrayMove(prev.imagePreviewUrls, oldIndex, newIndex),
		}));
	}, []);

	const imageSortSensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
	);

	const {
		getRootProps,
		getInputProps,
		isDragActive,
		isDragAccept,
		isDragReject,
	} = useDropzone({
		onDrop: addImages,
		accept: {
			"image/png": [".png"],
			"image/jpeg": [".jpg", ".jpeg"],
			"image/gif": [".gif"],
			"image/webp": [".webp"],
			"image/heic": [".heic"],
			"image/heif": [".heif"],
		},
		multiple: true,
	});

	const [paywallOpen, setPaywallOpen] = useState(false);
	const [slugAvailable, setSlugAvailable] = useState(false);
	const [slugAvailabilityError, setSlugAvailabilityError] = useState<
		string | null
	>(null);
	const [slugAvailabilityLoading, setSlugAvailabilityLoading] = useState(false);

	const handleCreate = useCallback(() => {
		const value = getPageTotalPrice(form.imageFiles.length);
		trackCheckoutStarted(value, form.imageFiles.length);
		setPaywallOpen(true);
	}, [form.imageFiles.length, trackCheckoutStarted]);

	const canNext = useMemo(() => {
		if (step === 0)
			return (
				form.slug.trim().length > 0 &&
				form.coupleName1.trim().length > 0 &&
				form.coupleName2.trim().length > 0 &&
				slugAvailable &&
				!slugAvailabilityError &&
				!slugAvailabilityLoading
			);
		if (step === 1) return form.title.trim().length > 0;
		return true;
	}, [
		step,
		form,
		slugAvailable,
		slugAvailabilityError,
		slugAvailabilityLoading,
	]);

	const totalSteps = STEPS.length;
	const isLast = step === totalSteps - 1;
	const extraPhotoCount = getExtraPhotoCount(form.imageFiles.length);
	const totalPrice = getPageTotalPrice(form.imageFiles.length);

	const checkSlugAvailability = useCallback(async () => {
		const slug = form.slug.trim();
		if (!slug) {
			setSlugAvailable(false);
			setSlugAvailabilityError(null);
			return;
		}
		setSlugAvailabilityLoading(true);
		setSlugAvailabilityError(null);
		try {
			const res = await fetch(
				`/api/page/check-slug?slug=${encodeURIComponent(slug)}`,
			);
			const data = (await res.json()) as {
				available?: boolean;
				error?: string;
			};
			if (!res.ok) {
				setSlugAvailable(false);
				setSlugAvailabilityError(data.error ?? t("form.urlAvailabilityError"));
				return;
			}
			if (data.available) {
				setSlugAvailable(true);
				setSlugAvailabilityError(null);
			} else {
				setSlugAvailable(false);
				setSlugAvailabilityError(t("form.urlTaken"));
			}
		} catch {
			setSlugAvailable(false);
			setSlugAvailabilityError(t("form.urlAvailabilityError"));
		} finally {
			setSlugAvailabilityLoading(false);
		}
	}, [form.slug, t]);

	return (
		<div className="min-h-screen w-full bg-background relative">
			{/* Warm Soft Coral & Cream */}
			<div
				className="absolute inset-0 z-0 dark:opacity-40"
				style={{
					backgroundImage: `
        radial-gradient(circle at 20% 80%, rgba(255, 160, 146, 0.25) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(255, 244, 228, 0.3) 0%, transparent 50%),
        radial-gradient(circle at 40% 40%, rgba(255, 160, 146, 0.15) 0%, transparent 50%)`,
				}}
			/>
			<div className="relative z-10">
				{/* Your Content/Components */}
				<div className="min-h-screen text-foreground">
					<header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur">
						<div className="container mx-auto flex h-14 items-center justify-between px-4">
							<Logo />
							<div className="flex items-center gap-4">
								<span className="text-sm text-muted-foreground">
									{t("header")}
								</span>
								<LanguageToggle />
								<ModeToggle />
							</div>
						</div>
					</header>

					<main className="container mx-auto px-4 py-8 md:py-12">
						<div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-2">
							{/* Coluna direita no desktop: preview. Mobile: ordem 1 (acima do form) */}
							<div className="order-2 lg:order-2 lg:sticky lg:top-24 lg:self-start">
								<PagePreview data={form} />
							</div>
							{/* Coluna esquerda no desktop: stepper + form. Mobile: ordem 2 (abaixo do preview) */}
							<div className="order-1 flex flex-col gap-8 lg:order-1">
								{/* Stepper: em cima do formulário */}
								<div className="flex flex-wrap items-center justify-center gap-2">
									{STEPS.map((s, i) => {
										const Icon = s.icon;
										const active = step === i;
										const done = step > i;
										const canGoToStep = i <= step;
										return (
											<button
												key={s.id}
												type="button"
												onClick={() => canGoToStep && setStep(i)}
												disabled={!canGoToStep}
												className={cn(
													"flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition-colors",
													active &&
														"border-primary bg-primary/10 text-primary ring-2 ring-primary/20",
													done && !active && "border-primary/50 text-primary",
													!active &&
														!done &&
														canGoToStep &&
														"border-border text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground",
													!canGoToStep &&
														"cursor-not-allowed border-border opacity-50 text-muted-foreground",
												)}
											>
												<Icon
													className={cn(
														"h-4 w-4",
														(active || done) && "text-primary",
													)}
													aria-hidden
												/>
											</button>
										);
									})}
								</div>
								<Card className="border-border">
									<CardHeader>
										<CardTitle className="flex items-center gap-2">
											{(() => {
												const Icon = STEPS[step]?.icon ?? FileText;
												return (
													<Icon className="h-5 w-5 text-primary" aria-hidden />
												);
											})()}
											{STEPS[step]?.label}
										</CardTitle>
										<CardDescription>{STEP_DESCRIPTIONS[step]}</CardDescription>
									</CardHeader>
									<CardContent className="space-y-6">
										{step === 0 && (
											<>
												<div className="space-y-2">
													<Label htmlFor="slug">{t("form.pageUrl")}</Label>
													<Input
														id="slug"
														placeholder="ana-joao"
														maxLength={25}
														onBlur={() => {
															checkSlugAvailability();
														}}
														value={form.slug}
														onChange={(e) => {
															setSlugAvailable(false);
															setSlugAvailabilityError(null);
															update({
																slug: e.target.value
																	.toLowerCase()
																	.replace(/[^a-z0-9-]/g, "-")
																	.replace(/-+/g, "-"),
															});
														}}
													/>
													<div className="flex items-center gap-1">
														<p className="text-xs text-muted-foreground">
															{t("form.pageUrlHint")}
															{form.slug || ""}
														</p>
														{slugAvailable &&
															!slugAvailabilityLoading &&
															!slugAvailabilityError && (
																<Check className="h-4 w-4 text-green-500" />
															)}
														{slugAvailabilityLoading && (
															<Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
														)}
														{slugAvailabilityError && (
															<X className="h-4 w-4 text-destructive" />
														)}
													</div>
													{slugAvailabilityError && (
														<p className="text-sm text-destructive">
															{slugAvailabilityError}
														</p>
													)}
												</div>
												<div className="grid gap-4 sm:grid-cols-2">
													<div className="space-y-2">
														<Label htmlFor="name1">{t("form.name1")}</Label>
														<Input
															id="name1"
															placeholder="Ana"
															maxLength={25}
															value={form.coupleName1}
															onChange={(e) =>
																update({ coupleName1: e.target.value })
															}
														/>
													</div>
													<div className="space-y-2">
														<Label htmlFor="name2">{t("form.name2")}</Label>
														<Input
															id="name2"
															placeholder="João"
															maxLength={25}
															value={form.coupleName2}
															onChange={(e) =>
																update({ coupleName2: e.target.value })
															}
														/>
													</div>
												</div>
											</>
										)}

										{step === 1 && (
											<>
												<div className="space-y-2">
													<Label htmlFor="title">{t("form.pageTitle")}</Label>
													<Input
														id="title"
														placeholder="Nosso amor"
														maxLength={40}
														value={form.title}
														onChange={(e) => update({ title: e.target.value })}
													/>
												</div>
												<div className="space-y-2">
													<Label htmlFor="message">{t("form.message")}</Label>
													<Textarea
														id="message"
														placeholder={t("form.messagePlaceholder")}
														rows={5}
														maxLength={400}
														value={form.message}
														onChange={(e) =>
															update({ message: e.target.value })
														}
													/>
												</div>
												<div className="space-y-2">
													<Label htmlFor="date">
														{t("form.relationshipDate")}
													</Label>
													<Input
														id="date"
														type="date"
														value={form.relationshipDate}
														onChange={(e) =>
															update({ relationshipDate: e.target.value })
														}
													/>
												</div>
											</>
										)}

										{step === 2 && (
											<>
												<div className="space-y-2">
													<Label>{t("form.photoGallery")}</Label>
													<div
														{...getRootProps()}
														className={cn(
															"flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed py-8 transition-colors",
															isDragAccept &&
																"border-green-500 bg-green-500/10",
															isDragReject &&
																"border-destructive bg-destructive/10",
															isDragActive &&
																!isDragAccept &&
																!isDragReject &&
																"border-primary bg-primary/10",
															!isDragActive &&
																"border-border bg-muted/30 hover:border-primary/50 hover:bg-muted/50",
														)}
													>
														<input {...getInputProps()} />
														<Plus className="mb-2 h-10 w-10 text-muted-foreground" />
														<span className="text-sm font-medium text-muted-foreground">
															{t("form.addPhotos")}
														</span>
														<span className="mt-1 text-xs text-muted-foreground">
															{t("form.photosSelected", {
																count: form.imageFiles.length,
															})}
														</span>
													</div>
												</div>
												{form.imagePreviewUrls.length > 0 && (
													<DndContext
														sensors={imageSortSensors}
														collisionDetection={closestCenter}
														onDragEnd={(event: DragEndEvent) => {
															const { active, over } = event;
															if (!over || active.id === over.id) return;
															const urls = form.imagePreviewUrls;
															const oldIndex = urls.indexOf(
																active.id as string,
															);
															const newIndex = urls.indexOf(over.id as string);
															if (oldIndex !== -1 && newIndex !== -1) {
																reorderImages(oldIndex, newIndex);
															}
														}}
													>
														<SortableContext
															items={form.imagePreviewUrls}
															strategy={rectSortingStrategy}
														>
															<div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
																{form.imagePreviewUrls.map((url, i) => (
																	<SortableImageItem
																		key={url}
																		id={url}
																		url={url}
																		onRemove={() => removeImage(i)}
																		removeLabel={t("form.removePhoto")}
																	/>
																))}
															</div>
														</SortableContext>
													</DndContext>
												)}
											</>
										)}

										{step === 3 && (
											<div className="space-y-2">
												<Label htmlFor="music">{t("form.musicUrl")}</Label>
												<Input
													id="music"
													type="url"
													placeholder="https://..."
													value={form.musicUrl}
													onChange={(e) => update({ musicUrl: e.target.value })}
												/>
												<p className="text-xs text-muted-foreground">
													{t("form.musicUrlHint")}
												</p>
											</div>
										)}

										{step === 4 && (
											<div className="space-y-4 rounded-xl border border-border bg-muted/20 p-4">
												<p className="text-sm font-medium">
													{t("review.summary")}
												</p>
												<dl className="grid gap-2 text-sm">
													<div>
														<dt className="text-muted-foreground">
															{t("review.url")}
														</dt>
														<dd>
															{env.NEXT_PUBLIC_BASE_URL}/{form.slug || "—"}
														</dd>
													</div>
													<div>
														<dt className="text-muted-foreground">
															{t("review.couple")}
														</dt>
														<dd>
															{form.coupleName1 || "—"} &{" "}
															{form.coupleName2 || "—"}
														</dd>
													</div>
													<div>
														<dt className="text-muted-foreground">
															{t("review.title")}
														</dt>
														<dd>{form.title || "—"}</dd>
													</div>
													{form.imageFiles.length > 0 && (
														<div>
															<dt className="text-muted-foreground">
																{t("review.photos")}
															</dt>
															<dd>
																{form.imageFiles.length}{" "}
																{t("review.photosSelected")}
															</dd>
															{extraPhotoCount > 0 && (
																<dd className="text-xs text-primary">
																	{t("review.extraPhotos", {
																		count: extraPhotoCount,
																		price: extraPhotoCount,
																	})}
																</dd>
															)}
														</div>
													)}

													<div>
														<dt className="text-muted-foreground">
															{t("review.value")}
														</dt>
														<dd className="text-lg">
															R$ {formatPriceBRL(totalPrice)}
														</dd>
													</div>
												</dl>
												<div className="space-y-2 pt-2">
													<Label htmlFor="coupon-code">
														{t("review.couponLabel")}
													</Label>
													<Input
														id="coupon-code"
														value={couponCode}
														onChange={(e) => setCouponCode(e.target.value)}
														placeholder={t("review.couponPlaceholder")}
													/>
													<p className="text-xs text-muted-foreground">
														{t("review.couponHint")}
													</p>
												</div>
											</div>
										)}

										{/* Navigation */}
										<div className="flex flex-wrap items-center justify-between gap-4 pt-4">
											<Button
												type="button"
												variant="outline"
												onClick={() => setStep((s) => Math.max(0, s - 1))}
												disabled={step === 0}
											>
												<ChevronLeft className="h-4 w-4" />
												{t("buttons.back")}
											</Button>
											{!isLast ? (
												<Button
													type="button"
													onClick={() => {
														trackFormStep(step, STEPS[step]?.label ?? "");
														setStep((s) => s + 1);
													}}
													disabled={!canNext}
												>
													{t("buttons.next")}
													<ChevronRight className="h-4 w-4" />
												</Button>
											) : (
												<Button type="button" onClick={handleCreate}>
													<Heart className="h-4 w-4" />
													{t("buttons.createPage")}
												</Button>
											)}
										</div>
									</CardContent>
								</Card>
							</div>
						</div>
					</main>

					<PaymentModal
						open={paywallOpen}
						onClose={() => setPaywallOpen(false)}
						form={form}
						couponCode={couponCode}
					/>
				</div>
			</div>
		</div>
	);
}
